import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Megaphone,
  Users,
  BarChart2,
  Play,
  X,
  CheckCircle,
  XCircle,
  Clock,
  Send,
  MailX,
  AlertTriangle,
  ThumbsDown,
  PackageCheck,
  RotateCcw,
  Link2,
  Loader2,
} from 'lucide-react';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { Button } from '../components/ui/Button.tsx';
import { Badge, CampaignStatusBadge, ChannelBadge } from '../components/ui/Badge.tsx';
import { Skeleton } from '../components/ui/Skeleton.tsx';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.tsx';
import { Pagination } from '../components/ui/Pagination.tsx';
import { useToast } from '../components/ui/Toast.tsx';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Campaign {
  id: string;
  name: string;
  channel: string;
  status: string;
  totalRecipients: number;
  sentCount: number;
  failedCount: number;
  bounceCount: number;
  complaintCount: number;
  deliveredCount: number;
  pauseReason: string | null;
  scheduledAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  templateId: string | null;
  segmentId: string | null;
  lygoShortlinksEnabled: boolean;
}

interface CampaignStats {
  campaignId: string;
  total: number;
  pending: number;
  sent: number;
  failed: number;
  skipped: number;
  bounced: number;
  complained: number;
  delivered: number;
  deliveredCount: number;
  bounceCount: number;
  complaintCount: number;
  bounceRate: number | null;
  complaintRate: number | null;
  deliveryRate: number | null;
  autoPaused: boolean;
  pauseReason: string | null;
}

interface Recipient {
  id: string;
  customerId: string;
  recipientAddress: string | null;
  firstName: string | null;
  lastName: string | null;
  status: string;
  sentAt: string | null;
  failedAt: string | null;
  failureReason: string | null;
  errorMessage: string | null;
  skippedReason: string | null;
  openedAt: string | null;
  clickedAt: string | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(d: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function StatCard({
  label,
  value,
  sub,
  icon,
}: {
  label: string;
  value: React.ReactNode;
  sub?: string;
  icon?: React.ReactNode;
}) {
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-5 flex items-start gap-4">
      {icon && <span className="text-zinc-400 mt-0.5">{icon}</span>}
      <div className="min-w-0">
        <p className="text-xs text-zinc-500 font-medium">{label}</p>
        <p className="text-2xl font-semibold text-zinc-900 tabular-nums mt-0.5">{value}</p>
        {sub && <p className="text-xs text-zinc-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5 border-b border-zinc-50 last:border-0">
      <span className="text-xs text-zinc-500 shrink-0">{label}</span>
      <span className="text-xs text-zinc-800 font-medium text-right break-all">{value ?? '—'}</span>
    </div>
  );
}

const RECIPIENT_STATUS_MAP: Record<
  string,
  { variant: 'success' | 'danger' | 'warning' | 'default' | 'info' }
> = {
  sent: { variant: 'success' },
  delivered: { variant: 'success' },
  failed: { variant: 'danger' },
  bounced: { variant: 'danger' },
  complained: { variant: 'danger' },
  pending: { variant: 'warning' },
  skipped: { variant: 'default' },
  opened: { variant: 'info' },
  clicked: { variant: 'purple' as never },
};

const RECIPIENT_STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'sent', label: 'Sent' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'bounced', label: 'Bounced' },
  { value: 'complained', label: 'Complained' },
  { value: 'failed', label: 'Failed' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'pending', label: 'Pending' },
];

// ─── Page ─────────────────────────────────────────────────────────────────────

export function CampaignDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const [recipientPage, setRecipientPage] = useState(1);
  const [recipientStatusFilter, setRecipientStatusFilter] = useState('');
  const recipientLimit = 50;
  const [confirmLaunch, setConfirmLaunch] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmResume, setConfirmResume] = useState(false);

  // ── queries ─────────────────────────────────────────────────────────────────

  const { data: campaign, isLoading } = useQuery<Campaign>({
    queryKey: ['campaign', id],
    queryFn: () => api.get(`/api/v1/campaigns/${id}`).then((r) => r.data),
    enabled: !!id,
  });

  const { data: stats } = useQuery<CampaignStats>({
    queryKey: ['campaign-stats', id],
    queryFn: () => api.get(`/api/v1/campaigns/${id}/stats`).then((r) => r.data),
    enabled: !!id,
    refetchInterval:
      campaign?.status === 'running' || campaign?.status === 'paused' ? 10_000 : false,
  });

  const { data: recipientsData } = useQuery<{ items: Recipient[]; total: number }>({
    queryKey: ['campaign-recipients', id, recipientPage, recipientStatusFilter],
    queryFn: () =>
      api
        .get(`/api/v1/campaigns/${id}/recipients`, {
          params: {
            page: recipientPage,
            limit: recipientLimit,
            ...(recipientStatusFilter ? { status: recipientStatusFilter } : {}),
          },
        })
        .then((r) => r.data),
    enabled: !!id,
  });

  const recipients = recipientsData?.items ?? [];
  const totalPages = Math.max(1, Math.ceil((recipientsData?.total ?? 0) / recipientLimit));

  // ── mutations ────────────────────────────────────────────────────────────────

  const launchMutation = useMutation({
    mutationFn: () => api.post(`/api/v1/campaigns/${id}/launch`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaign', id] });
      qc.invalidateQueries({ queryKey: ['campaign-stats', id] });
      toast.success('Campaign launched');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const cancelMutation = useMutation({
    mutationFn: () => api.post(`/api/v1/campaigns/${id}/cancel`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaign', id] });
      toast.success('Campaign cancelled');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const resumeMutation = useMutation({
    mutationFn: () => api.post(`/api/v1/campaigns/${id}/resume`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaign', id] });
      qc.invalidateQueries({ queryKey: ['campaign-stats', id] });
      toast.success('Campaign resumed');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  // ── lygo shortlinks ───────────────────────────────────────────────────────────

  const { data: lygoLinksData, refetch: refetchLygoLinks } = useQuery({
    queryKey: ['campaign-shortlinks', id],
    queryFn: () => api.get(`/api/v1/campaigns/${id}/shortlinks`).then((r) => r.data),
    enabled: !!campaign?.lygoShortlinksEnabled,
  });

  const toggleLygoMutation = useMutation({
    mutationFn: (enabled: boolean) =>
      api.patch(`/api/v1/campaigns/${id}`, { lygoShortlinksEnabled: enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaign', id] }),
    onError: (err) => toast.error(extractError(err)),
  });

  const generateLygoMutation = useMutation({
    mutationFn: () => api.post(`/api/v1/campaigns/${id}/shortlinks/generate`, {}),
    onSuccess: () => {
      refetchLygoLinks();
      toast.success('I link sono stati creati con successo');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const { data: integrations } = useQuery({
    queryKey: ['integrations'],
    queryFn: () => api.get('/api/v1/integrations').then((r) => r.data),
  });
  const lygoConfigured = !!(integrations as Record<string, unknown>)?.lygo;

  // ── loading state ─────────────────────────────────────────────────────────────

  if (isLoading || !campaign) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const canLaunch = campaign.status === 'draft' || campaign.status === 'scheduled';
  const canCancel = campaign.status === 'running' || campaign.status === 'scheduled';
  const canResume = campaign.status === 'paused';

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/campaigns')}
          className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors"
        >
          <ArrowLeft size={16} />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-semibold text-zinc-900 truncate">{campaign.name}</h1>
          <p className="text-xs text-zinc-400 font-mono mt-0.5">{campaign.id}</p>
        </div>
        <ChannelBadge channel={campaign.channel} />
        <CampaignStatusBadge status={campaign.status} pauseReason={campaign.pauseReason} />
        {canLaunch && (
          <Button
            size="sm"
            onClick={() => setConfirmLaunch(true)}
            loading={launchMutation.isPending}
          >
            <Play size={12} className="mr-1" /> Launch
          </Button>
        )}
        {canResume && (
          <Button
            size="sm"
            onClick={() => setConfirmResume(true)}
            loading={resumeMutation.isPending}
          >
            <RotateCcw size={12} className="mr-1" /> Resume
          </Button>
        )}
        {canCancel && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setConfirmCancel(true)}
            loading={cancelMutation.isPending}
          >
            <X size={12} className="mr-1" /> Cancel
          </Button>
        )}
      </div>

      {/* Auto-pause circuit breaker banner */}
      {stats?.autoPaused && (
        <div className="flex items-start gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl">
          <AlertTriangle size={16} className="text-red-500 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-red-800">Campaign automatically paused</p>
            <p className="text-xs text-red-600 mt-0.5">
              {stats.pauseReason === 'auto_bounce_rate'
                ? `Bounce rate ${stats.bounceRate != null ? (stats.bounceRate * 100).toFixed(2) + '%' : ''} exceeded the 5% safety threshold.`
                : stats.pauseReason === 'auto_complaint_rate'
                  ? `Complaint rate ${stats.complaintRate != null ? (stats.complaintRate * 100).toFixed(3) + '%' : ''} exceeded the 0.1% safety threshold.`
                  : 'Delivery safety threshold exceeded.'}{' '}
              Review the issue and resume or cancel the campaign manually.
            </p>
          </div>
        </div>
      )}

      {/* Stats — row 1: send-time counts */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            label="Total recipients"
            value={stats.total.toLocaleString()}
            icon={<Users size={16} />}
          />
          <StatCard
            label="Sent"
            value={stats.sent.toLocaleString()}
            sub={
              stats.total > 0
                ? `${Math.round((stats.sent / stats.total) * 100)}% of total`
                : undefined
            }
            icon={<Send size={16} />}
          />
          <StatCard
            label="Pending"
            value={stats.pending.toLocaleString()}
            sub={stats.skipped > 0 ? `${stats.skipped} skipped` : undefined}
            icon={<Clock size={16} />}
          />
          <StatCard
            label="Failed"
            value={stats.failed.toLocaleString()}
            icon={<XCircle size={16} />}
          />
        </div>
      )}

      {/* Stats — row 2: post-send delivery feedback */}
      {stats &&
        (stats.deliveredCount > 0 ||
          stats.bounceCount > 0 ||
          stats.complaintCount > 0 ||
          campaign?.status === 'completed') && (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <StatCard
              label="Delivered"
              value={stats.deliveredCount.toLocaleString()}
              sub={
                stats.deliveryRate != null
                  ? `${(stats.deliveryRate * 100).toFixed(1)}% delivery rate`
                  : undefined
              }
              icon={<PackageCheck size={16} className="text-emerald-500" />}
            />
            <StatCard
              label="Bounced"
              value={stats.bounceCount.toLocaleString()}
              sub={
                stats.bounceRate != null
                  ? `${(stats.bounceRate * 100).toFixed(2)}% bounce rate`
                  : undefined
              }
              icon={
                <MailX
                  size={16}
                  className={
                    stats.bounceRate != null && stats.bounceRate > 0.05
                      ? 'text-red-500'
                      : stats.bounceRate != null && stats.bounceRate > 0.02
                        ? 'text-amber-500'
                        : 'text-zinc-400'
                  }
                />
              }
            />
            <StatCard
              label="Complaints"
              value={stats.complaintCount.toLocaleString()}
              sub={
                stats.complaintRate != null
                  ? `${(stats.complaintRate * 100).toFixed(3)}% complaint rate`
                  : undefined
              }
              icon={
                <ThumbsDown
                  size={16}
                  className={
                    stats.complaintRate != null && stats.complaintRate > 0.001
                      ? 'text-red-500'
                      : stats.complaintRate != null && stats.complaintRate > 0.0008
                        ? 'text-amber-500'
                        : 'text-zinc-400'
                  }
                />
              }
            />
          </div>
        )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left — campaign info */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-5 py-3.5 border-b border-zinc-100">
              <Megaphone size={14} className="text-zinc-400" />
              <h2 className="text-sm font-semibold text-zinc-700">Campaign info</h2>
            </div>
            <div className="p-5">
              <KV label="Channel" value={<ChannelBadge channel={campaign.channel} />} />
              <KV label="Status" value={<CampaignStatusBadge status={campaign.status} />} />
              <KV
                label="Template"
                value={
                  campaign.templateId ? (
                    <code className="font-mono text-[10px]">{campaign.templateId}</code>
                  ) : (
                    '—'
                  )
                }
              />
              <KV
                label="Segment"
                value={
                  campaign.segmentId ? (
                    <code className="font-mono text-[10px]">{campaign.segmentId}</code>
                  ) : (
                    'All customers'
                  )
                }
              />
              <KV label="Scheduled" value={fmt(campaign.scheduledAt)} />
              <KV label="Started" value={fmt(campaign.startedAt)} />
              <KV label="Completed" value={fmt(campaign.completedAt)} />
              <KV label="Created" value={fmt(campaign.createdAt)} />
            </div>
          </div>

          {/* Progress bar */}
          {campaign.totalRecipients > 0 && (
            <div className="mt-4 bg-white border border-zinc-200 rounded-xl p-5">
              <p className="text-xs font-medium text-zinc-600 mb-2 flex items-center gap-1.5">
                <BarChart2 size={12} className="text-zinc-400" />
                Send progress
              </p>
              <div className="w-full h-2 bg-zinc-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-500 rounded-full transition-all"
                  style={{
                    width: `${Math.round((campaign.sentCount / campaign.totalRecipients) * 100)}%`,
                  }}
                />
              </div>
              <div className="flex justify-between mt-1.5">
                <span className="text-[10px] text-zinc-400">
                  {campaign.sentCount.toLocaleString()} sent
                </span>
                <span className="text-[10px] text-zinc-400">
                  {campaign.totalRecipients.toLocaleString()} total
                </span>
              </div>
              {campaign.failedCount > 0 && (
                <p className="text-[10px] text-red-500 mt-1">{campaign.failedCount} failed</p>
              )}
            </div>
          )}

          {/* Lygo Shortlinks */}
          {lygoConfigured && campaign.status === 'draft' && (
            <div className="mt-4 bg-white border border-zinc-200 rounded-xl p-5 space-y-4">
              <div className="flex items-center gap-2">
                <Link2 className="w-4 h-4 text-zinc-600" />
                <h3 className="text-sm font-semibold text-zinc-900">Lygo Shortlinks</h3>
              </div>

              {/* Toggle */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-600">
                  Converti URL in shortlink lygo prima dell'invio
                </span>
                <button
                  type="button"
                  onClick={() => toggleLygoMutation.mutate(!campaign.lygoShortlinksEnabled)}
                  disabled={toggleLygoMutation.isPending}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${
                    campaign.lygoShortlinksEnabled ? 'bg-blue-600' : 'bg-zinc-300'
                  }`}
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                      campaign.lygoShortlinksEnabled ? 'translate-x-[18px]' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>

              {/* Generate button + list */}
              {campaign.lygoShortlinksEnabled && (
                <>
                  <button
                    type="button"
                    onClick={() => generateLygoMutation.mutate()}
                    disabled={generateLygoMutation.isPending}
                    className="flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 disabled:opacity-50"
                  >
                    {generateLygoMutation.isPending ? (
                      <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generazione…</>
                    ) : (
                      <><Link2 className="w-3.5 h-3.5" /> Genera shortlink</>
                    )}
                  </button>

                  {/* Link list */}
                  {((lygoLinksData as { links: Array<{ originalUrl: string; shortUrl: string }> } | undefined)?.links?.length ?? 0) > 0 && (
                    <div className="space-y-1.5">
                      <p className="text-xs font-medium text-zinc-500">
                        {(lygoLinksData as { links: unknown[] }).links.length} link pronti
                      </p>
                      {(lygoLinksData as { links: Array<{ originalUrl: string; shortUrl: string }> }).links
                        .slice(0, 5)
                        .map((l) => (
                          <div key={l.originalUrl} className="grid grid-cols-[1fr_auto_1fr] gap-1 items-center text-xs">
                            <span className="text-zinc-400 truncate" title={l.originalUrl}>{l.originalUrl}</span>
                            <span className="text-zinc-300">→</span>
                            <span className="text-blue-600 font-mono truncate">{l.shortUrl}</span>
                          </div>
                        ))}
                      {(lygoLinksData as { links: unknown[] }).links.length > 5 && (
                        <p className="text-xs text-zinc-400">
                          +{(lygoLinksData as { links: unknown[] }).links.length - 5} altri…
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Right — recipients table */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-100">
              <div className="flex items-center gap-2">
                <Users size={14} className="text-zinc-400" />
                <h2 className="text-sm font-semibold text-zinc-700">
                  Recipients
                  {recipientsData ? ` · ${recipientsData.total.toLocaleString()}` : ''}
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <select
                    value={recipientStatusFilter}
                    onChange={(e) => {
                      setRecipientStatusFilter(e.target.value);
                      setRecipientPage(1);
                    }}
                    className="h-7 pl-2.5 pr-6 bg-white border border-zinc-200 rounded-lg text-xs text-zinc-700 focus:outline-none appearance-none"
                  >
                    {RECIPIENT_STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <span className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-zinc-400 text-[9px]">
                    ▾
                  </span>
                </div>
                {campaign.status === 'running' && (
                  <span className="flex items-center gap-1.5 text-[11px] text-zinc-400">
                    <Clock size={11} /> Live
                  </span>
                )}
              </div>
            </div>

            {recipients.length === 0 ? (
              <div className="p-10 text-center">
                <Users size={24} className="text-zinc-300 mx-auto mb-2" />
                <p className="text-sm text-zinc-400">No recipients yet</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-zinc-100">
                        <th className="px-5 pb-2 pt-3 text-left text-zinc-400 font-medium">
                          Customer
                        </th>
                        <th className="px-5 pb-2 pt-3 text-left text-zinc-400 font-medium">
                          Status
                        </th>
                        <th className="px-5 pb-2 pt-3 text-left text-zinc-400 font-medium">
                          Reason
                        </th>
                        <th className="px-5 pb-2 pt-3 text-left text-zinc-400 font-medium">
                          Sent at
                        </th>
                        <th className="px-5 pb-2 pt-3 text-left text-zinc-400 font-medium">
                          Opened
                        </th>
                        <th className="px-5 pb-2 pt-3 text-left text-zinc-400 font-medium">
                          Clicked
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-50">
                      {recipients.map((r) => {
                        const errorMsg = r.errorMessage ?? r.failureReason;
                        return (
                        <tr key={r.id} className={`hover:bg-zinc-50 transition-colors ${r.status === 'failed' ? 'bg-red-50/30' : ''}`}>
                          <td className="px-5 py-2.5">
                            <button
                              onClick={() => navigate(`/customers/${r.customerId}`)}
                              className="text-[11px] text-zinc-700 hover:text-zinc-900 hover:underline"
                            >
                              {r.firstName || r.lastName
                                ? `${r.firstName ?? ''} ${r.lastName ?? ''}`.trim()
                                : r.customerId.slice(0, 8) + '…'}
                            </button>
                            {r.recipientAddress && (
                              <div className="text-[10px] text-zinc-400 font-mono">{r.recipientAddress}</div>
                            )}
                          </td>
                          <td className="px-5 py-2.5">
                            <Badge variant={RECIPIENT_STATUS_MAP[r.status]?.variant ?? 'default'}>
                              {r.status}
                            </Badge>
                          </td>
                          <td className="px-5 py-2.5">
                            {r.status === 'skipped' && r.skippedReason ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-zinc-100 text-zinc-600 border border-zinc-200">
                                {r.skippedReason === 'no_consent'
                                  ? 'No consent'
                                  : r.skippedReason === 'customer_deleted'
                                    ? 'Customer deleted'
                                    : r.skippedReason === 'suppressed'
                                      ? 'Email suppressed'
                                      : r.skippedReason}
                              </span>
                            ) : r.status === 'failed' && errorMsg ? (
                              <div className="max-w-xs">
                                <p className="text-[11px] text-red-600 font-medium">
                                  {errorMsg.includes('MONTHLY_SPEND_LIMIT')
                                    ? 'AWS SMS monthly spend limit reached'
                                    : errorMsg.includes('Service Quota Exceeded')
                                      ? `Service quota exceeded: ${errorMsg.split('Reason=')[1]?.replace(/"/g, '') ?? errorMsg}`
                                      : errorMsg.includes('MessageRejected')
                                        ? 'Email rejected by provider'
                                        : errorMsg.length > 80
                                          ? errorMsg.slice(0, 80) + '…'
                                          : errorMsg}
                                </p>
                                {errorMsg.length > 80 && (
                                  <details className="mt-0.5">
                                    <summary className="text-[9px] text-red-400 cursor-pointer">Full error</summary>
                                    <pre className="text-[9px] text-red-500 mt-0.5 whitespace-pre-wrap break-all">{errorMsg}</pre>
                                  </details>
                                )}
                              </div>
                            ) : (
                              <span className="text-zinc-300">—</span>
                            )}
                          </td>
                          <td className="px-5 py-2.5 text-zinc-500 whitespace-nowrap">
                            {fmt(r.sentAt)}
                          </td>
                          <td className="px-5 py-2.5">
                            {r.openedAt ? (
                              <CheckCircle size={12} className="text-emerald-500" />
                            ) : (
                              <span className="text-zinc-300">—</span>
                            )}
                          </td>
                          <td className="px-5 py-2.5">
                            {r.clickedAt ? (
                              <CheckCircle size={12} className="text-emerald-500" />
                            ) : (
                              <span className="text-zinc-300">—</span>
                            )}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {totalPages > 1 && (
                  <div className="border-t border-zinc-100">
                    <Pagination
                      page={recipientPage}
                      totalPages={totalPages}
                      onPageChange={setRecipientPage}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Confirm launch */}
      <ConfirmDialog
        open={confirmLaunch}
        title="Launch campaign?"
        message={`"${campaign.name}" will be sent immediately to all targeted recipients. This cannot be undone.`}
        confirmLabel="Launch"
        variant="warning"
        onConfirm={() => {
          setConfirmLaunch(false);
          launchMutation.mutate();
        }}
        onCancel={() => setConfirmLaunch(false)}
      />

      {/* Confirm cancel */}
      <ConfirmDialog
        open={confirmCancel}
        title="Cancel campaign?"
        message={`"${campaign.name}" will be stopped. Recipients already sent will not be affected.`}
        confirmLabel="Cancel campaign"
        variant="danger"
        onConfirm={() => {
          setConfirmCancel(false);
          cancelMutation.mutate();
        }}
        onCancel={() => setConfirmCancel(false)}
      />

      {/* Confirm resume */}
      <ConfirmDialog
        open={confirmResume}
        title="Resume campaign?"
        message={`"${campaign.name}" will resume sending to remaining recipients. Make sure the underlying delivery issue has been resolved before resuming.`}
        confirmLabel="Resume"
        variant="warning"
        onConfirm={() => {
          setConfirmResume(false);
          resumeMutation.mutate();
        }}
        onCancel={() => setConfirmResume(false)}
      />
    </div>
  );
}
